import React from 'react';
import { Info } from 'lucide-react';
import { Database } from '../../types/database.types';
import { planMediaLine } from '../../lib/planAllowance';
import { Skel } from '../ui/PageState';

export type Plan = Database['public']['Tables']['plans']['Row'];

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const pt = new Intl.NumberFormat('pt-BR');

export function isDefaultPlan(p: Plan) {
  return p.name === 'Creator' || p.name === 'individual';
}

type Props = {
  plans: Plan[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Linhas de recursos sob os créditos (sem a linha de créditos). */
  featuresOf: (plan: Plan) => string[];
  disabled?: boolean;
  legend?: string;
};

/**
 * Seletor de plano como grupo de radios nativos: navegável por setas, foco visível,
 * alvo de toque inteiro no cartão. Estilos `.plan-option*` em index.css.
 */
export function PlanOptions({ plans, selectedId, onSelect, featuresOf, disabled, legend = 'Escolha seu plano' }: Props) {
  return (
    <fieldset className="register-plans" disabled={disabled}>
      <legend className="register-section-title">{legend}</legend>
      <ul className="plan-options">
        {plans.map((p) => {
          const selected = selectedId === p.id;
          const mediaHint = planMediaLine(p.credits_per_month);
          const features = featuresOf(p);
          return (
            <li key={p.id}>
              <label className={`plan-option ${selected ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="plan"
                  value={p.id}
                  checked={selected}
                  onChange={() => onSelect(p.id)}
                  className="plan-option-input"
                />
                <span className="plan-option-radio" aria-hidden="true" />
                <span className="plan-option-body">
                  <span className="plan-option-head">
                    <span className="plan-option-name">{p.name}</span>
                    {isDefaultPlan(p) && <span className="plan-badge">Recomendado</span>}
                  </span>
                  <span className="plan-option-credits">
                    {pt.format(p.credits_per_month)} créditos/mês
                    <button
                      type="button"
                      className="plan-info"
                      aria-label={mediaHint}
                      onClick={(e) => e.preventDefault()}
                    >
                      <Info size={14} aria-hidden />
                      <span className="plan-info-tip" role="tooltip" aria-hidden="true">{mediaHint}</span>
                    </button>
                  </span>
                  {features.length > 0 && (
                    <ul className="plan-option-features">
                      {features.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  )}
                </span>
                <span className="plan-option-price">
                  {usd.format(Number(p.monthly_price))}
                  <small>/mês</small>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

export function PlanOptionsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="register-plans" aria-hidden>
      <Skel w={140} h={12} style={{ marginBottom: 14 }} />
      <ul className="plan-options">
        {Array.from({ length: count }).map((_, i) => (
          <li key={i}>
            <Skel className="" h={76} style={{ borderRadius: 12 }} />
          </li>
        ))}
      </ul>
    </div>
  );
}
